import { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import {
  Grid,
  FormControl,
  Typography,
  MenuItem,
  IconButton,
  Tooltip,
  Autocomplete,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import clsx from 'clsx'
import {
  CustomInputLabel,
  CustomSelect,
  CustomTextField,
} from '../restyled-mui/CustomInputs'
import DateRangePicker from './DateRangePicker/DateRangePicker'
import HelpIcon from '@mui/icons-material/Help'
import { PrimaryButton } from '../restyled-mui/CustomButton'
import LinearProgress from '@mui/material/LinearProgress'
import Api from '../../api'
import { setSelectedUserSetup } from '../../redux/actions/userSetups'
import { setPCDateRange, setPCPlant } from '../../redux/actions/powerCurve'
import moment from 'moment'

const useStyles = makeStyles(theme => ({
  formContainer: {
    backgroundColor: theme.palette.common.white,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: '15px 25px',
  },
  customSubtitle: {
    fontSize: 14,
    color: theme.palette.primary.main,
    marginBottom: '10px',
  },
  sectionDelimiter: {
    paddingTop: '20px',
  },
  runButtonDelimiter: {
    paddingTop: '40px',
  },
  runButton: {
    width: '100%',
  },
  tooltip: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    fontSize: theme.typography.fontSize,
  },
  arrow: {
    color: theme.palette.primary.main,
  },
  helpIcon: {
    color: theme.palette.primary.main,
  },
  tooltipContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: '4px',
  },
}))

const PowerCurveForm = props => {
  const classes = useStyles(props)
  const {
    selectedSetup,
    userPowerSetups,
    setSelectedUserSetup,
    selectedDateRange,
    setPCDateRange,
    selectedPlant,
    setPCPlant,
    onRun,
    disableButton,
    trackDisableStatus,
  } = props

  const validatedSetup = userPowerSetups.includes(selectedSetup)
    ? selectedSetup
    : null

  const isEmptySetups =
    userPowerSetups.length === 0 || userPowerSetups.some(x => x === undefined)

  const [plants, setPlants] = useState(null)
  const [plantLoading, setPlantLoading] = useState(false)
  const [plantsError, setPlantsError] = useState('')

  const [timeDisableBtn, setTimeDisableBtn] = useState(false)

  useEffect(() => {
    const fetchPlants = async () => {
      setPlantLoading(true)
      try {
        const response = await Api.Metrics.getSetupPlants(
          validatedSetup,
          selectedDateRange,
        )
        if (response.status === 200) {
          if (response.data.length !== 0) {
            setPlants(response.data)
            setPlantsError('')
          } else {
            const startDate = moment(selectedDateRange.startDate).format(
              'YYYY-MM-DD',
            )
            const endDate = moment(selectedDateRange.endDate).format(
              'YYYY-MM-DD',
            )
            setPlants(null)
            setPlantsError(
              `No plants for selected period: ${startDate} to ${endDate}.`,
            )
          }
        }
      } catch (error) {
        setPlants(null)
        setPlantsError('Error fetching plants for selected setup')
        console.error('Error fetching plants:', error)
      } finally {
        setPlantLoading(false)
      }
    }

    if (validatedSetup !== null) {
      fetchPlants()
    } else {
      setPlants(null)
    }
  }, [validatedSetup, selectedDateRange])

  const handleSetupChange = (_, newValue) => {
    setPCPlant(null)
    setSelectedUserSetup(newValue)
  }

  const canRun = validatedSetup && selectedPlant && !timeDisableBtn

  return (
    <div className={classes.formContainer}>
      <Grid container spacing={0}>
        <Grid item md={12}>
          <Typography
            variant="h4"
            className={clsx(classes.customSubtitle)}
            style={{ marginBottom: 0 }}
          >
            Setup selection
          </Typography>

          {!isEmptySetups ? (
            <Grid item md={12}>
              <div className={classes.tooltipContainer}>
                <CustomInputLabel>Setup Name</CustomInputLabel>
                <Tooltip
                  title="You can search for a value by typing in the box!"
                  placement="top"
                  arrow
                  classes={{ tooltip: classes.tooltip, arrow: classes.arrow }}
                >
                  <span>
                    <IconButton
                      style={{ padding: '0' }}
                      aria-label="info"
                      disableTouchRipple
                    >
                      <HelpIcon fontSize="small" className={classes.helpIcon} />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
              <FormControl fullWidth variant="outlined" size="small">
                <Autocomplete
                  disabled={trackDisableStatus || userPowerSetups.length === 1}
                  variant="outlined"
                  size="small"
                  options={userPowerSetups}
                  value={validatedSetup}
                  autoHighlight
                  onChange={handleSetupChange}
                  renderInput={params => (
                    <CustomTextField
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        style: { padding: '1px 5px' },
                      }}
                    />
                  )}
                />
              </FormControl>
            </Grid>
          ) : (
            <CustomInputLabel>
              No available setup for selection or error occurred.
            </CustomInputLabel>
          )}

          {validatedSetup && (
            <>
              <Grid item md={12} className={classes.sectionDelimiter}>
                <Typography variant="h4" className={classes.customSubtitle}>
                  Date selection
                </Typography>
                <Grid container spacing={2}>
                  <DateRangePicker
                    trackDisableStatus={trackDisableStatus}
                    initialDateRange={selectedDateRange}
                    onDateRangeChange={dateRange => {
                      setPCDateRange(dateRange)
                      setPCPlant(null)
                    }}
                    onErrorChange={setTimeDisableBtn}
                  />
                </Grid>
              </Grid>

              <Grid item md={12} className={classes.sectionDelimiter}>
                <Typography variant="h4" className={classes.customSubtitle}>
                  Configuration
                </Typography>

                {plantLoading ? (
                  <LinearProgress style={{ marginTop: '20px' }} />
                ) : plants !== null ? (
                  <>
                    <CustomInputLabel>Plant</CustomInputLabel>
                    <FormControl fullWidth variant="outlined" size="small">
                      <Autocomplete
                        disabled={trackDisableStatus}
                        variant="outlined"
                        size="small"
                        options={plants}
                        value={selectedPlant}
                        autoHighlight
                        onChange={(_, newPlant) => setPCPlant(newPlant)}
                        renderInput={params => (
                          <CustomTextField
                            {...params}
                            inputProps={{
                              ...params.inputProps,
                              style: { padding: '1px 5px' },
                            }}
                          />
                        )}
                      />
                    </FormControl>
                  </>
                ) : (
                  <CustomInputLabel style={{ color: 'orangered' }}>
                    {plantsError}
                  </CustomInputLabel>
                )}

                {canRun && (
                  <div className={classes.runButtonDelimiter}>
                    <PrimaryButton
                      onClick={onRun}
                      className={classes.runButton}
                      disabled={disableButton}
                      style={{ background: disableButton ? '#ccc' : undefined }}
                    >
                      RUN
                    </PrimaryButton>
                  </div>
                )}
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
    </div>
  )
}

const mapStateToProps = state => ({
  selectedSetup: state.userSetups.selectedSetup,
  userPowerSetups: state.userSetups.userPowerSetups,
  selectedDateRange: state.powerCurve.selectedDateRange,
  selectedPlant: state.powerCurve.selectedPlant,
})

const mapDispatchToProps = {
  setSelectedUserSetup,
  setPCDateRange,
  setPCPlant,
}

export default connect(mapStateToProps, mapDispatchToProps)(PowerCurveForm)
